
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  MoreHorizontalIcon,
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  AlertTriangleIcon,
} from 'lucide-react';
import { Payment, useUpdatePayment, useDeletePayment } from '@/api/payments';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { formatCurrency } from '@/lib/format-currency';
import { AddInstallmentDialog } from '@/components/payments/add-installment-dialog';
import { UpgradePaymentDialog } from '@/components/payments/upgrade-payment-dialog';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirmationStore } from '@/lib/stores/confirmation-store';
interface UserPaymentsProps {
  userId: string;
  payments?: any[];
  onPaymentUpdate?: () => void;
  user?: any; // User data to check remaining entries
}

export function UserPayments({
  userId,
  payments = [],
  onPaymentUpdate,
  user,
}: UserPaymentsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const confirmationStore = useConfirmationStore();
  const updatePaymentMutation = useUpdatePayment();
  const deletePaymentMutation = useDeletePayment();

  // State for add installment dialog
  const [addInstallmentDialogOpen, setAddInstallmentDialogOpen] =
    useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  // State for upgrade dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [paymentToUpgrade, setPaymentToUpgrade] = useState<Payment | null>(
    null
  );

  // Handler for adding installments
  const handleAddInstallment = (payment: Payment) => {
    setSelectedPayment(payment);
    setAddInstallmentDialogOpen(true);
  };

  // Handler for upgrading payment
  const handleUpgrade = (payment: Payment) => {
    setPaymentToUpgrade(payment);
    setUpgradeDialogOpen(true);
  };

  // Check if payment is active
  const isPaymentActive = (payment: Payment) => {
    // If payment has an endDate, check if it's in the future
    if (payment.endDate) {
      return dayjs().isBefore(dayjs(payment.endDate));
    }

    // Otherwise, try to check if it's a fixed plan
    const plan = typeof payment.planId === 'object' ? payment.planId : null;
    if (plan && plan.durationType === 'fixed') {
      // For fixed plans without endDate: check if user has remaining entries
      return user?.remainingEntries && user?.remainingEntries > 0;
    }

    // If we can't determine, assume inactive
    return false;
  };

  const handleInstallmentAdded = async (
    paymentId: string,
    newInstallments: any[]
  ) => {
    if (!paymentId) return;

    try {
      await updatePaymentMutation.mutateAsync({
        id: paymentId,
        payment: {
          installments: newInstallments,
        },
      });

      toast({
        title: t('common.success'),
        description: t('payments.installmentAdded'),
      });

      setAddInstallmentDialogOpen(false);

      // Call the callback to refresh user data
      if (onPaymentUpdate) {
        onPaymentUpdate();
      }
    } catch (error) {
      console.error('Error adding installment:', error);
      toast({
        title: t('common.error'),
        description: t('payments.installmentAddError'),
        variant: 'destructive',
      });
    }
  };

  // Handler for deleting payments
  const handleDeletePayment = async (id: string) => {
    confirmationStore.open({
      title: t('payments.confirmDelete'),
      message: t('payments.deleteConfirmation'),
      variant: 'destructive',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        try {
          await deletePaymentMutation.mutateAsync({ id, gymId: '' }); // gymId is still required by API

          // Force close the confirmation dialog
          confirmationStore.close();

          toast({
            title: t('common.success'),
            description: t('payments.paymentDeleted'),
          });

          // Call the callback to refresh user data
          if (onPaymentUpdate) {
            onPaymentUpdate();
          }
        } catch (error) {
          console.error('Error deleting payment:', error);
          toast({
            title: t('common.error'),
            description: t('payments.deleteError', 'Failed to delete payment'),
            variant: 'destructive',
          });
        }
      },
    });
  };

  // Calculate paid amount from installments
  const getPaidAmount = (payment: Payment) => {
    if (!payment.installments || payment.installments.length === 0) {
      return payment.amount;
    }
    return payment.installments.reduce(
      (sum, inst) => sum + Number(inst.amount),
      0
    );
  };

  // Get plan currency from payment
  const getPlanCurrency = (payment: Payment) => {
    if (typeof payment.planId === 'object' && payment.planId !== null) {
      return payment.planId.currency || 'ALL';
    }
    return 'ALL';
  };

  // Helper function to safely format date
  const formatDate = (dateString: any): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Determine currently active plans (not deleted, not expired, within date range or fixed with remaining entries)
  const activePlans = payments.filter((p) => {
    if (p.deleted || p.expired) return false
    if (p.endDate) {
      if (p.endDate === 'fixed') {
        return typeof user?.remainingEntries === 'number' && user.remainingEntries > 0
      }
      return dayjs().isBefore(dayjs(p.endDate))
    }
    const plan = typeof p.planId === 'object' ? p.planId : null
    if (plan && plan.durationType === 'fixed') {
      return typeof user?.remainingEntries === 'number' && user.remainingEntries > 0
    }
    return false
  })

  if (!payments || payments.length === 0) {
    return <div className="text-center py-8">{t('users.noPayments')}</div>;
  }

  // Filter out deleted payments
  const activePayments = payments.filter((payment) => payment.deleted !== true);

  if (activePayments.length === 0) {
    return <div className="text-center py-8">{t('users.noPayments')}</div>;
  }

  return (
    <div className="space-y-6">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('payments.date')}</TableHead>
            <TableHead>{t('users.activePlan', 'Plan')}</TableHead>
            <TableHead>{t('payments.amount')}</TableHead>
            <TableHead>{t('payments.method')}</TableHead>
            <TableHead>{t('payments.status')}</TableHead>
            <TableHead>{t('payments.expired', 'Expired')}</TableHead>
            <TableHead>{t('payments.details')}</TableHead>
            <TableHead>{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activePayments.map((payment) => {
            const paidAmount = getPaidAmount(payment);
            const isActive = isPaymentActive(payment);
            const plan = typeof payment.planId === 'object' && payment.planId !== null ? payment.planId : null;
            const planName = plan?.name ?? (payment.planId ? String(payment.planId) : '-');
            const isFixedPlan = payment.endDate === 'fixed' || plan?.durationType === 'fixed';
            return (
              <TableRow key={payment?._id || Math.random().toString()}>
                <TableCell>{formatDate(payment?.date)}</TableCell>
                {/* Plan cell with hover card */}
                <TableCell>
                  <HoverCard openDelay={150} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <button className="text-sm font-medium underline decoration-dotted underline-offset-2 cursor-pointer hover:text-primary transition-colors text-left">
                        {planName}
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-72" align="start">
                      {plan ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm">{plan.name}</span>
                            {isActive && !payment.expired && (
                              <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-xs">
                                {t('users.active')}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <span className="text-muted-foreground">{t('payments.amount', 'Price')}</span>
                            <span className="font-medium">{formatCurrency(plan.price, plan.currency ?? 'ALL')}</span>

                            <span className="text-muted-foreground">{t('plans.duration', 'Duration')}</span>
                            <span className="font-medium">
                              {plan.durationType === 'fixed'
                                ? t('users.fixedPlan', 'Fixed')
                                : `${plan.duration ?? '-'} ${plan.durationType ?? ''}`}
                            </span>

                            {plan.daysPerMonth != null && (
                              <>
                                <span className="text-muted-foreground">{t('plans.daysPerMonth', 'Days / month')}</span>
                                <span className="font-medium">{plan.daysPerMonth}</span>
                              </>
                            )}

                            {plan.isClass && (
                              <>
                                <span className="text-muted-foreground">{t('plans.type', 'Type')}</span>
                                <span className="font-medium">{t('plans.classPlan', 'Class Plan')}</span>
                              </>
                            )}

                            <span className="text-muted-foreground">{t('users.startDate', 'Start')}</span>
                            <span className="font-medium">{formatDate(payment.date)}</span>

                            <span className="text-muted-foreground">{t('users.endDate', 'End')}</span>
                            <span className="font-medium">
                              {isFixedPlan
                                ? t('users.fixedPlan', 'Fixed')
                                : payment.endDate
                                ? formatDate(payment.endDate)
                                : '-'}
                            </span>

                            {isFixedPlan && typeof user?.remainingEntries === 'number' && (
                              <>
                                <span className="text-muted-foreground">{t('users.remaining', 'Remaining')}</span>
                                <span className="font-medium">{user.remainingEntries}</span>
                              </>
                            )}
                          </div>
                          {plan.description && (
                            <p className="text-xs text-muted-foreground border-t pt-2 mt-1">{plan.description}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('plans.noPlanDetails', 'No plan details available')}</p>
                      )}
                    </HoverCardContent>
                  </HoverCard>
                </TableCell>
                <TableCell>
                  {formatCurrency(paidAmount, getPlanCurrency(payment))}
                  {payment?.isOffer && (
                    <span className="text-sm text-muted-foreground ml-1">
                      ({t('payments.offer', 'Offer')})
                    </span>
                  )}
                </TableCell>
                <TableCell>{payment?.method || 'N/A'}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      payment?.status === 'completed' ||
                      payment?.status === 'paid'
                        ? 'default'
                        : payment?.status === 'expired'
                        ? 'destructive'
                        : payment?.status === 'partially_paid'
                        ? 'secondary'
                        : payment?.status === 'free'
                        ? 'outline'
                        : 'secondary'
                    }
                  >
                    {payment?.status === 'completed' ||
                    payment?.status === 'paid'
                      ? t('payments.paid')
                      : payment?.status === 'expired'
                      ? t('payments.statusExpired')
                      : payment?.status === 'partially_paid'
                      ? t('payments.partiallyPaid')
                      : payment?.status === 'free'
                      ? t('payments.free')
                      : t('payments.pending')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={payment?.expired ? 'destructive' : 'outline'}
                    >
                      {payment?.expired
                        ? t('common.yes', 'Yes')
                        : t('common.no', 'No')}
                      {payment?.expiredAt && (
                        <span className="ml-1">
                          ({formatDate(payment.expiredAt)})
                        </span>
                      )}
                    </Badge>
                    {payment?.expired && payment?.expiredReason && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangleIcon className="h-4 w-4 text-amber-500 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {String(
                                t(
                                  `payments.expiredReasons.${payment.expiredReason}`,
                                  payment.expiredReason
                                )
                              )}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {payment?.installments &&
                  Array.isArray(payment.installments) &&
                  payment.installments.length > 1 ? (
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="installments">
                        <AccordionTrigger className="text-xs py-1">
                          {t('payments.viewInstallments')}
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="text-xs space-y-1">
                            {payment.installments.map(
                              (installment: any, index: number) => (
                                <div
                                  key={index}
                                  className="flex justify-between py-1 border-b"
                                >
                                  <span>{formatDate(installment?.date)}</span>
                                  <span>
                                    {formatCurrency(installment?.amount)}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {t('payments.singlePayment')}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {isActive && !payment?.expired && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleUpgrade(payment)}
                      >
                        <ArrowUpIcon className="h-4 w-4 mr-1" />
                        {t('payments.upgrade', 'Upgrade')}
                      </Button>
                    )}
                    {payment?.status === 'partially_paid' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddInstallment(payment)}
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        {t('payments.addInstallment')}
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontalIcon className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleDeletePayment(payment._id || '')}
                          className="text-destructive"
                        >
                          <TrashIcon className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Add Installment Dialog */}
      {selectedPayment && (
        <AddInstallmentDialog
          open={addInstallmentDialogOpen}
          onOpenChange={setAddInstallmentDialogOpen}
          payment={selectedPayment}
          onInstallmentAdded={handleInstallmentAdded}
        />
      )}

      {/* Upgrade Payment Dialog */}
      {paymentToUpgrade && (
        <UpgradePaymentDialog
          open={upgradeDialogOpen}
          onOpenChange={setUpgradeDialogOpen}
          payment={paymentToUpgrade}
          onSuccess={onPaymentUpdate}
        />
      )}
    </div>
  );
}
